import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { Center, Loader, MantineProvider } from "@mantine/core";
import { Notifications, showNotification } from "@mantine/notifications";

import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";

import * as Sentry from "@sentry/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import theme from "./theme.tsx";
import routes from "./router/routes.tsx";
import StoreProvider from "./redux/store";

import "./config/i18n";
import { AxiosError } from "axios";
import { initSentry } from "./sentry";
import { SentryErrorFallback } from "./components/SentryErrorFallback";

initSentry();

const root = document.getElementById("root");

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          let message = "Something went wrong. Please try again.";
          if (status === 401) {
            message = "Your session has expired. Please sign in again.";
          } else if (status === 403) {
            message = "You don't have permission to perform this action.";
          } else if (status === 429) {
            message = "Too many requests. Please wait a moment and try again.";
          } else if (status !== undefined && status >= 500) {
            message = "Something went wrong on our end. Please try again later.";
            Sentry.captureException(error, { tags: { source: "react-query-mutation" } });
          } else {
            const backendMsg = error.response?.data?.message;
            if (backendMsg && !/authorization header|token|jwt|internal server/i.test(backendMsg)) {
              message = backendMsg;
            }
          }
          showNotification({ message, color: "red" });
          return;
        }
        Sentry.captureException(error, { tags: { source: "react-query-mutation" } });
        showNotification({
          message: "Something went wrong. Please try again.",
          color: "red",
        });
      },
    },
  },
});

if (root) {
  createRoot(root).render(
    <StrictMode>
      <StoreProvider>
        <MantineProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
            <Notifications position="top-left" />
            <Suspense
              fallback={
                <Center mih="50dvh">
                  <Loader size="md" type="dots" aria-label="Loading" />
                </Center>
              }
            >
              <Sentry.ErrorBoundary
                fallback={({ error, resetError }) => (
                  <SentryErrorFallback error={error} resetError={resetError} />
                )}
                showDialog={false}
              >
                <RouterProvider router={routes} />
              </Sentry.ErrorBoundary>
            </Suspense>
          </QueryClientProvider>
        </MantineProvider>
      </StoreProvider>
    </StrictMode>
  );
}
